import { withFayeClient } from 'javascripts/faye_client';
import AssistantChat from '../../framework/models/AssistantChat';
import AssistantMessage from '../../framework/models/AssistantMessage';
import AssistantToolCall from '../../framework/models/AssistantToolCall';
import Attachment from '../../framework/models/Attachment';
import { type MessageRole_value } from '../../framework/models/generated/types';
import AssistantContent from '../../framework/models/AssistantContent';
import { type AgentParams } from '../agents/Agent';
import type Agent from '../agents/Agent';
import {
  deepUnderscoreKeys,
  deepCamelizeKeys,
} from '../../../javascripts/keyCamelizer';
import secureRandomHexString from 'javascripts/util/secureRandom';
import { showAlertMessage } from 'javascripts/messages';
import { type Instruction } from '../components/AssistantView/AssistantViewController';
import type AssistantViewController from '../components/AssistantView/AssistantViewController';
import { jsonFetch } from 'javascripts/fetch';
import { AssistantArtifact } from 'components/framework/models';
import Tool from '../agent_tools/Tool';

// The assistant model is the data structure representing the current state of
// the assistant.
class Assistant {
  // Currently active chat.
  currentChat: AssistantChat;

  // Whether communication to the server is currently active (preventing user
  // input and cancelable),
  active: boolean = false;

  // Whether we are currently loading a conversation.
  loading: boolean = false;

  // Unique ID for this assistantistant instance. This is so that when creating
  // a new chat, only this instance processes the messages.
  uniqueAssistantId: string;

  // Faye subscription to the chat messages.
  subscription: any = null;

  fayeClient: any = null;

  // Chunk buffers for reordering out-of-order streaming chunks
  messageTextChunks: Map<
    RecordId,
    Array<{ sequence: number; content: string }>
  > = new Map();
  messageReasoningChunks: Map<
    RecordId,
    Array<{ sequence: number; content: string }>
  > = new Map();
  toolCallContentChunks: Map<
    string,
    Array<{ sequence: number; content: any }>
  > = new Map();

  constructor(private readonly controller: AssistantViewController) {
    this.currentChat = new AssistantChat({ messages: [], safe: true });
    this.uniqueAssistantId = secureRandomHexString(16);
  }

  get fayeChannelName() {
    return `/ai_agents/${window.currentAccount!.id}/${window.currentUser!.id}`;
  }

  subscribe() {
    withFayeClient(client => {
      this.fayeClient = client;

      const channelName = this.fayeChannelName;
      this.setSubscription(client, channelName);

      client.onSubscriptionAuthenticationFailure(async message => {
        if (message.subscription !== channelName) return;

        await this.resubscribe();
      });
    });
  }

  ensureFayeConnected() {
    if (
      this.fayeClient &&
      this.fayeClient._state === this.fayeClient.DISCONNECTED
    ) {
      // Copied from fayes source https://github.com/faye/faye/blob/master/src/protocol/client.js#L357
      this.fayeClient._state = this.fayeClient.UNCONNECTED; // Force to unconnected state
      this.fayeClient._dispatcher.clientId = null;
      this.fayeClient._cycleConnection();

      void this.resubscribe();
    }
  }

  async resubscribe() {
    if (!this.fayeClient) {
      console.error('No Faye client to resubscribe with');
      return null;
    }

    const token = await this.fetchNewSubscriptionToken();
    if (!token) {
      return null;
    }

    $('meta[name=ai-agents-token]').attr('content', token);

    this.unsubscribe();
    this.setSubscription(this.fayeClient, this.fayeChannelName);
  }

  setSubscription(client, channelName: string) {
    this.subscription = client.subscribe(channelName, msg => {
      this.handleOperation(msg);
    });
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.cancel();
      this.subscription = null;
    }
  }

  async fetchNewSubscriptionToken(): Promise<string | null> {
    const response = await jsonFetch('/assistant/chats/refresh_faye_token', {});
    if (!response.ok) {
      const error = await response.text();
      console.error(`Failed to fetch new subscription token: ${error}`);
      return null;
    }
    const data = (await response.json()) as { token: string };
    if (!data.token) {
      console.error('No token received from server');
      return null;
    }
    return data.token;
  }

  get emptyChat(): boolean {
    return (
      this.currentChat &&
      this.currentChat.messages.length === 0 &&
      !this.loading
    );
  }

  get isChatFrozen(): boolean {
    return Boolean(this.currentChat && this.currentChat.frozen);
  }

  get currentMessage(): AssistantMessage | null {
    return this.currentChat.messages[this.currentChat.messages.length - 1];
  }

  lastMessage(role: MessageRole_value): AssistantMessage | undefined {
    return this.currentChat.messages.findLast(message => message.role === role);
  }

  lastInstructions(): Array<Instruction> | null {
    const lastMessageWithInstructions = [...this.currentChat.messages]
      .reverse()
      .find(message => message.scope?.instructions);

    return lastMessageWithInstructions?.scope?.instructions ?? null;
  }

  newChat(agentName: string) {
    this.active = false;
    this.currentChat = new AssistantChat({
      agent: agentName,
      safe: true,
      messages: [],
    });
    this.messageTextChunks.clear();
    this.messageReasoningChunks.clear();
    this.toolCallContentChunks.clear();
  }

  async loadChat(idOrToken: string): Promise<void> {
    this.loading = true;
    this.active = false;
    this.messageTextChunks.clear();
    this.messageReasoningChunks.clear();
    this.toolCallContentChunks.clear();
    try {
      this.currentChat = await AssistantChat.select([
        'id',
        'agent',
        'name',
        'safe',
        'token',
        'frozen',
        'stopReason',
      ])
        .merge({
          messages: AssistantMessage.select([
            'id',
            'parentId',
            'role',
            'safe',
            'content',
            'reasoning',
            'scope',
          ]).merge({
            toolCalls: AssistantToolCall.select([
              'id',
              'tool',
              'icon',
              'status',
              'collapsable',
            ]).merge({
              content: AssistantContent.select(['data']).merge({
                preview: [
                  'title',
                  'contentType',
                  'body',
                  'autoCollapse',
                  'subtitle',
                ],
                panel: ['title', 'contentType', 'body'],
                actionButton: ['label'],
              }),
              artifact: AssistantArtifact.select([
                'id',
                'token',
                'contentType',
              ]),
            }),
            attachments: Attachment.select([
              'id',
              'contentType',
              'fileName',
              'downloadUrl',
            ]),
          }),
        })
        .find(idOrToken);

      console.debug(`Loaded Chat ID ${idOrToken}`);
    } finally {
      this.loading = false;
    }
  }

  // Used by suggestions panel to add a suggestion as an assistant message.
  // To prompt the user to add more context.
  // Will be overridden when the create chat event is handled.
  addSuggestionAsAssistantMessage(content: string): AssistantMessage {
    if (this.currentChat.messages.length > 0) {
      throw new Error(
        'Cannot add suggestion as assistant message when there are existing messages.'
      );
    }

    const message = new AssistantMessage({
      role: 'assistant',
      parentId: this.currentMessage?.id || null,
      content,
      attachments: [],
      scope: {},
      safe: true,
      toolCalls: [],
    });
    this.currentChat.messages.push(message);
    return message;
  }

  // Add a user message to the chat and send it to the server.
  async sendUserMessage(
    content: string,
    attachments: Attachment[],
    scope: Record<string, string>,
    options: {
      parentId?: RecordId | 'error';
      agent?: Agent;
      agentParams?: AgentParams;
    } = {}
  ) {
    // NOTE: Filter out error messages (id: 'error') and unsaved messages (id: undefined)
    // to prevent invalid parent_id from causing thread branching issues.
    const lastMessage = this.currentChat.messages.findLast(
      m => m.id !== undefined && (m.id as RecordId | 'error') !== 'error'
    );
    const message = new AssistantMessage({
      role: 'user',
      parentId:
        options.parentId === 'error'
          ? lastMessage?.id
          : options.parentId || lastMessage?.id || null,
      content,
      attachments,
      scope,
      toolCalls: [],
    });
    this.currentChat.messages.push(message);

    const payload = {
      id: this.currentChat.id,
      agent: options.agent?.name(),
      ...(options.agentParams && {
        agent_params: deepUnderscoreKeys(options.agentParams),
      }),
      message: {
        text: content,
        attachments: attachments.map(attachment => ({
          id: attachment.id,
          filename: attachment.fileName,
          content_type: attachment.contentType,
        })),
        scope: deepUnderscoreKeys(scope),
      },
      unavailable_tools: Tool.unavailableTools(options.agent),
      parent_id: options.parentId,
      thread_id: this.currentChat.currentThreadId,
      client_id: this.uniqueAssistantId,
    };

    await this.sendToServer('/assistant/chats', payload);
  }

  retryMessage(message: AssistantMessage) {
    this.cancel();

    // Remove all messages including and after message.
    const index = this.currentChat.messages.indexOf(message);
    if (index !== -1) {
      this.currentChat.messages.splice(index);
    }

    // Resubmit the message to the server, adding it to the previous parent
    // to make a new branch in the messages tree.
    this.sendUserMessage(message.content, message.attachments, message.scope, {
      parentId: message.parentId || '0', // Pass 0 to indicate the root message.
    });
  }

  removeMessagesAfter(message: AssistantMessage) {
    // Remove all messages after message.
    const index = this.currentChat.messages.indexOf(message);
    if (index !== -1) {
      this.currentChat.messages.splice(index + 1);
    }
  }

  // Convert the message content to a "create_file_tool" call.
  convertToArtifact(message: AssistantMessage) {
    // TODO: not implemented yet.
    // TODO: This is hard because we also need to generate the summary and the file_name.
  }

  approveToolCalls(
    toolCalls: {
      toolCall: AssistantToolCall;
      data?: Record<string, unknown> | string;
    }[]
  ) {
    toolCalls.map(toolCall => {
      // Optimistically update the tool call status.
      // @ts-ignore Writing locally but not saving to server.
      toolCall.toolCall.status = 'processing';
      void this.sendToServer(
        `/assistant/chats/${this.currentChat.id}/approve`,
        {
          approved_tool_call_id: toolCall.toolCall.id,
          ...(toolCall.data && {
            approved_with: toolCall.data,
          }),
        },
        {
          method: 'PATCH',
        }
      );
    });
  }

  rejectToolCalls(toolCalls: AssistantToolCall[]) {
    // Optimistically update the tool call status.
    toolCalls.forEach(toolCall => {
      // @ts-ignore Writing locally but not saving to server.
      toolCall.status = 'rejected';
      void this.sendToServer(
        `/assistant/chats/${this.currentChat.id}/reject`,
        {
          rejected_tool_call_id: toolCall.id,
        },
        {
          method: 'PATCH',
        }
      );
    });
  }

  provideFrontendContent(toolCall: AssistantToolCall, content: Object | null) {
    // Optimistically update the tool call status.
    // @ts-ignore Writing locally but not saving to server.
    toolCall.status = 'processing';
    void this.sendToServer(
      `/assistant/chats/${this.currentChat.id}/provide_frontend_content`,
      {
        tool_call_id: toolCall.id,
        content,
      },
      {
        method: 'PATCH',
      }
    );
  }

  // Cancel the in-flight request.
  async cancel() {
    if (!this.active) return;

    this.active = false;
    if (!this.currentChat.persisted) return;

    await fetch(`/assistant/chats/${this.currentChat.id}/cancel`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': window.csrfToken(),
      },
    }).catch(e => {
      const msg = `An error was caught in the assistant: ${e}`;
      console.error(msg, e);
      throw new Error(msg);
    });
  }

  // Send a message to the server. Responses are handled through Faye.
  async sendToServer(
    endpoint,
    message,
    { method } = { method: 'POST' }
  ): Promise<undefined | { id: string }> {
    this.ensureFayeConnected();
    this.active = true;

    const response = await fetch(endpoint, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': window.csrfToken(),
      },
      body: JSON.stringify(message),
    }).catch(e => {
      this.active = false;
      const msg = `An error was caught in the assistant: ${e}`;
      console.error(msg, e);
      throw new Error(msg);
    });

    const contentType = response.headers.get('content-type');
    if (response.ok && contentType?.includes('application/json')) {
      try {
        const data = (await response.json()) as {
          message?: {
            id: string;
          };
          error?: string;
        };
        if (data.error) {
          console.error(`Error from server: ${data.error}`);
          const message = new AssistantMessage({
            id: 'error',
            role: 'assistant',
            content: data.error,
            toolCalls: [],
          });
          this.currentChat.messages.push(message);
          this.active = false;
        } else {
          return data.message;
        }
      } catch (error) {
        console.error('Failed to parse response as JSON:', error);
      }
    } else if (!response.ok) {
      this.active = false;
      showAlertMessage(`The assistant could not handle the request`);
    }
  }

  handleOperation(data) {
    switch (data.op) {
      case 'createChat':
        if (data.clientId === this.uniqueAssistantId) {
          console.debug(`Creating Chat ID ${data.chat.id}`);
          const chat = new AssistantChat({
            id: data.chat.id,
            name: data.chat.name,
            safe: data.chat.safe,
            token: data.chat.token,
            currentThreadId: data.chat.currentThreadId,
            messages: [],
          });
          this.currentChat = chat;
          this.active = true;

          (data.chat.messages || []).forEach(messageData => {
            const message = new AssistantMessage({
              id: messageData.id,
              parentId: messageData.parentId,
              role: messageData.role,
              safe: messageData.safe || true,
              content: messageData.content,
              reasoning: messageData.reasoning,
              attachments: messageData.attachments,
              scope: messageData.scope,
              toolCalls: [],
            });
            this.currentChat.messages.push(message);
          });
        }
        break;

      case 'updateChat':
        if (data.chat.id === this.currentChat.id) {
          this.currentChat.name = data.chat.name;
        }
        break;

      case 'addMessage':
        {
          const messageData = data.message;

          if (messageData.chatId === this.currentChat.id) {
            this.active = true;
            const lastMessage = this.lastMessage('user');

            if (
              lastMessage &&
              lastMessage.id === undefined &&
              messageData.role === 'user'
            ) {
              // @ts-ignore Force ID assignment regardless of type constraints
              lastMessage.id = messageData.id;
            } else {
              const message = new AssistantMessage({
                id: messageData.id,
                parentId: messageData.parentId,
                role: messageData.role,
                safe: messageData.safe ?? true,
                content: messageData.content,
                reasoning: messageData.reasoning,
                scope: messageData.scope,
                attachments: messageData.attachments,
                toolCalls: [],
              });
              this.currentChat.messages.push(message);
            }
          }
        }
        break;

      case 'appendToMessage':
        {
          const message = this.currentChat.messages.findLast(
            message => message.id === data.id
          );
          if (message) {
            message.active = true;

            if (data.sequence !== undefined && data.sequence !== null) {
              // Use sequence-based reordering
              if (!this.messageTextChunks.has(message.id)) {
                this.messageTextChunks.set(message.id, []);
              }
              const chunks = this.messageTextChunks.get(message.id);
              if (!chunks) return;

              // NOTE: Performance optimization - 99.99% of chunks arrive in order
              const isInOrder =
                chunks.length === 0 ||
                data.sequence > chunks[chunks.length - 1].sequence;

              chunks.push({ sequence: data.sequence, content: data.content });

              if (isInOrder) {
                // Fast path: append to existing content (or replace if first chunk)
                if (chunks.length === 1) {
                  // @ts-expect-error Local update that won't be sent to the server.
                  message.content = data.content;
                } else {
                  // @ts-expect-error Local update that won't be sent to the server.
                  message.content += data.content;
                }
              } else {
                // Slow path: sort and rebuild (rare case for out-of-order chunks)
                chunks.sort((a, b) => a.sequence - b.sequence);
                // @ts-expect-error Local update that won't be sent to the server.
                message.content = chunks.map(c => c.content).join('');
              }
            } else {
              // Fallback to old behavior for backward compatibility
              // @ts-expect-error Local update that won't be sent to the server.
              message.content += data.content;
            }

            this.active = true;
          }
        }
        break;

      case 'appendReasoning':
        {
          const message = this.currentChat.messages.findLast(
            message => message.id === data.id
          );
          if (message) {
            message.active = true;
            if (data.reset) {
              // Clear chunks and reset with full content
              this.messageReasoningChunks.delete(message.id);
              // @ts-expect-error Local update that won't be sent to the server.
              message.reasoning = data.content;
            } else if (data.sequence !== undefined && data.sequence !== null) {
              // Use sequence-based reordering
              if (!this.messageReasoningChunks.has(message.id)) {
                this.messageReasoningChunks.set(message.id, []);
              }
              const chunks = this.messageReasoningChunks.get(message.id);
              if (!chunks) return;

              // NOTE: Performance optimization - 99.99% of chunks arrive in order
              const isInOrder =
                chunks.length === 0 ||
                data.sequence > chunks[chunks.length - 1].sequence;

              chunks.push({ sequence: data.sequence, content: data.content });

              if (isInOrder) {
                // Fast path: append to existing content (or replace if first chunk)
                if (chunks.length === 1) {
                  // @ts-expect-error Local update that won't be sent to the server.
                  message.reasoning = data.content;
                } else {
                  // @ts-expect-error Local update that won't be sent to the server.
                  message.reasoning = (message.reasoning || '') + data.content;
                }
              } else {
                // Slow path: sort and rebuild (rare case for out-of-order chunks)
                chunks.sort((a, b) => a.sequence - b.sequence);
                // @ts-expect-error Local update that won't be sent to the server.
                message.reasoning = chunks.map(c => c.content).join('');
              }
            } else {
              // Fallback to old behavior for backward compatibility
              // @ts-expect-error Local update that won't be sent to the server.
              message.reasoning = (message.reasoning || '') + data.content;
            }
            this.active = true;
          }
        }
        break;

      case 'stopMessage':
        {
          // Find the message to append to. Search backwards since the last
          // message is probably at the end.
          const message = this.currentChat.messages.findLast(
            message => message.id === data.id
          );
          if (message) {
            message.active = false;
          }
        }
        break;

      case 'addToolCall':
        {
          const toolCallData = data.toolCall;

          const message = this.currentChat.messages.findLast(
            message => message.id === toolCallData.messageId
          );

          if (message) {
            const toolCall = new AssistantToolCall({
              id: toolCallData.id,
              tool: toolCallData.tool,
              icon: toolCallData.icon,
              status: toolCallData.status,
              collapsable: toolCallData.collapsable,
              content: deepCamelizeKeys(toolCallData.content),
            });

            message.toolCalls.push(toolCall);
            this.active = true;
            this.controller.actionProcessToolCall(toolCall);
          }
        }
        break;

      case 'updateToolCall':
        {
          const toolCallData = data.toolCall;

          // Search messages in reverse order since the target message is likely recent
          for (let i = this.currentChat.messages.length - 1; i >= 0; i--) {
            const message = this.currentChat.messages[i];
            const toolCall = message.toolCalls.findLast(
              (tc: AssistantToolCall) => tc.id === toolCallData.id
            );
            if (toolCall) {
              // @ts-expect-error Local update that won't be sent to the server.
              toolCall.icon = toolCallData.icon;
              // @ts-expect-error Local update that won't be sent to the server.
              toolCall.status = toolCallData.status;
              // @ts-expect-error Local update that won't be sent to the server.
              toolCall.content = deepCamelizeKeys(toolCallData.content);
              // @ts-expect-error Local update that won't be sent to the server.
              toolCall.collapsable = toolCallData.collapsable;

              if (toolCallData.artifact) {
                // @ts-expect-error Local update that won't be sent to the server.
                toolCall.artifact = new AssistantArtifact({
                  id: toolCallData.artifact.id,
                  token: toolCallData.artifact.token,
                });
              }

              this.active = true;
              this.controller.actionProcessToolCall(toolCall);
              break;
            }
          }
        }
        break;

      case 'appendToToolCall':
        {
          const toolCallData = data.toolCall;

          // Search messages in reverse order since the target message is likely recent
          for (let i = this.currentChat.messages.length - 1; i >= 0; i--) {
            const message = this.currentChat.messages[i];
            const toolCall = message.toolCalls.findLast(
              (tc: AssistantToolCall) => tc.id === toolCallData.id
            );
            if (!toolCall) continue;

            const content = deepCamelizeKeys(toolCallData.content);

            if (
              toolCallData.sequence !== undefined &&
              toolCallData.sequence !== null
            ) {
              // Use sequence-based reordering
              if (!this.toolCallContentChunks.has(toolCall.id)) {
                this.toolCallContentChunks.set(toolCall.id, []);
              }
              const chunks = this.toolCallContentChunks.get(toolCall.id);
              if (!chunks) return;

              // NOTE: Performance optimization - 99.99% of chunks arrive in order
              const isInOrder =
                chunks.length === 0 ||
                toolCallData.sequence > chunks[chunks.length - 1].sequence;

              chunks.push({
                sequence: toolCallData.sequence,
                content: content,
              });

              // @ts-expect-error Local update that won't be sent to the server.
              toolCall.status = toolCallData.status;

              if (isInOrder) {
                // Fast path: apply delta without sorting
                this.applyToolCallDelta(toolCall, content);
              } else {
                // Slow path: sort and rebuild (rare case for out-of-order chunks)
                chunks.sort((a, b) => a.sequence - b.sequence);
                this.rebuildToolCallContent(toolCall, chunks);
              }
              this.active = true;
              break;
            } else if (toolCall.content) {
              // Fallback to old behavior for backward compatibility
              // @ts-expect-error Local update that won't be sent to the server.
              toolCall.status = toolCallData.status;

              this.applyToolCallDelta(toolCall, content);

              this.active = true;
              break;
            } else {
              // @ts-expect-error Local update that won't be sent to the server.
              toolCall.content = content;
            }
          }
        }
        break;

      case 'flagUnsafeMessages':
        if (data.chatId === this.currentChat.id) {
          // @ts-expect-error Local update that won't be sent to the server.
          this.currentChat.safe = false;
          this.active = true;

          const unsafeMessageIds = data.unsafeMessageIds;
          // Set the safe flag to false for all messages that are unsafe.
          this.currentChat.messages.forEach(message => {
            if (unsafeMessageIds.includes(message.id)) {
              // @ts-expect-error Local update that won't be sent to the server.
              message.safe = false;
            }
          });
        }
        break;

      case 'error':
        if (data.chatId === this.currentChat.id) {
          this.active = true;
          console.error(`Error from server: ${data.message}`);
          const message = new AssistantMessage({
            id: 'error',
            role: 'assistant',
            safe: true,
            content: data.message,
            toolCalls: [],
          });
          if (data.message.includes('No AI credits remaining')) {
            this.controller.actionSetUsageData({ noUsageAvailable: true });
          }
          this.currentChat.messages.push(message);
        }
        break;

      case 'endTurn':
        if (this.currentChat.id === data.chatId) {
          this.active = false;

          // Make sure all messages are finalized.
          this.currentChat.messages.forEach(message => {
            message.active = false;
          });

          this.currentChat.messages.forEach(message => {
            message.toolCalls.forEach(toolCall => {
              if (toolCall.status === 'needs_frontend_content') {
                this.controller.actionProvideToolCallData(toolCall);
              }
            });
          });

          if (data.usageData) {
            this.controller.actionSetUsageData(data.usageData);
          }
        }
        break;

      default:
        console.error(`Unknown operation: ${data.op}`);
    }
  }

  // Apply a single tool call delta (fast path for in-order chunks)
  private applyToolCallDelta(toolCall: AssistantToolCall, delta: any) {
    // Initialize content if needed
    if (!toolCall.content) {
      // @ts-expect-error Local update
      toolCall.content = {};
    }

    if (delta.preview) {
      if (toolCall.content?.preview) {
        if (delta.preview.title) {
          // @ts-expect-error
          toolCall.content.preview.title ||= '';
          // @ts-expect-error
          toolCall.content.preview.title += delta.preview.title;
        }
        if (delta.preview.body) {
          // @ts-expect-error
          toolCall.content.preview.body ||= '';
          // @ts-expect-error
          toolCall.content.preview.body += delta.preview.body;
        }
        // Copy over non-appending fields
        if (delta.preview.contentType) {
          // @ts-expect-error
          toolCall.content.preview.contentType = delta.preview.contentType;
        }
        if (delta.preview.autoCollapse !== undefined) {
          // @ts-expect-error
          toolCall.content.preview.autoCollapse = delta.preview.autoCollapse;
        }
        if (delta.preview.subtitle) {
          // @ts-expect-error
          toolCall.content.preview.subtitle = delta.preview.subtitle;
        }
      } else {
        // @ts-expect-error Local update that won't be sent to the server.
        toolCall.content.preview = delta.preview;
      }
    }

    // @ts-expect-error Local update that won't be sent to the server.
    toolCall.content.actionButton = delta.actionButton;
    // @ts-expect-error Local update that won't be sent to the server.
    toolCall.content.rejectButton = delta.rejectButton;

    if (delta.panel) {
      if (toolCall.content?.panel) {
        if (delta.panel.title) {
          toolCall.content.panel.title ||= '';
          toolCall.content.panel.title += delta.panel.title;
        }
        if (delta.panel.body) {
          toolCall.content.panel.body ||= '';
          toolCall.content.panel.body += delta.panel.body;
        }
        // Copy over non-appending fields
        if (delta.panel.contentType) {
          toolCall.content.panel.contentType = delta.panel.contentType;
        }
      } else {
        // @ts-expect-error Local update that won't be sent to the server.
        toolCall.content.panel = delta.panel;
      }
    }

    // NOTE delta.data is not handled here. ToolCallStreamer will produce an
    // updateToolCall if data is present, so there should never be data here
    if (delta.data) {
      console.warn('Data present in appendToToolCall message', delta.data);
    }
  }

  // Rebuild tool call content from sorted deltas
  private rebuildToolCallContent(
    toolCall: AssistantToolCall,
    chunks: Array<{ sequence: number; content: any }>
  ) {
    // Initialize fresh content structure
    // @ts-expect-error Local update
    toolCall.content = {};

    // Apply all deltas in order
    for (const chunk of chunks) {
      const delta = chunk.content;

      if (delta.preview) {
        // @ts-expect-error
        if (toolCall.content.preview) {
          if (delta.preview.title) {
            // @ts-expect-error
            toolCall.content.preview.title ||= '';
            // @ts-expect-error
            toolCall.content.preview.title += delta.preview.title;
          }
          if (delta.preview.body) {
            // @ts-expect-error
            toolCall.content.preview.body ||= '';
            // @ts-expect-error
            toolCall.content.preview.body += delta.preview.body;
          }
          // Copy over non-appending fields
          if (delta.preview.contentType) {
            // @ts-expect-error
            toolCall.content.preview.contentType = delta.preview.contentType;
          }
          if (delta.preview.autoCollapse !== undefined) {
            // @ts-expect-error
            toolCall.content.preview.autoCollapse = delta.preview.autoCollapse;
          }
          if (delta.preview.subtitle) {
            // @ts-expect-error
            toolCall.content.preview.subtitle = delta.preview.subtitle;
          }
        } else {
          // @ts-expect-error - Clone to avoid mutating the chunk array
          toolCall.content.preview = { ...delta.preview };
        }
      }

      if (delta.panel) {
        // @ts-expect-error
        if (toolCall.content.panel) {
          if (delta.panel.title) {
            // @ts-expect-error
            toolCall.content.panel.title ||= '';
            // @ts-expect-error
            toolCall.content.panel.title += delta.panel.title;
          }
          if (delta.panel.body) {
            // @ts-expect-error
            toolCall.content.panel.body ||= '';
            // @ts-expect-error
            toolCall.content.panel.body += delta.panel.body;
          }
          // Copy over non-appending fields
          if (delta.panel.contentType) {
            // @ts-expect-error
            toolCall.content.panel.contentType = delta.panel.contentType;
          }
        } else {
          // @ts-expect-error - Clone to avoid mutating the chunk array
          toolCall.content.panel = { ...delta.panel };
        }
      }

      // These replace rather than append (always assign, even if undefined)
      // @ts-expect-error
      toolCall.content.actionButton = delta.actionButton;
      // @ts-expect-error
      toolCall.content.rejectButton = delta.rejectButton;

      // NOTE: content.data is not handled here. ToolCallStreamer will produce an
      // updateToolCall if data is present, so there should never be data here
      if (delta.data) {
        console.warn('Data present in appendToToolCall message', delta.data);
      }
    }
  }

  // Development mode only: Update tool call status to replay it.
  async updateToolCallStatus(toolCall: AssistantToolCall, newStatus: string) {
    if (
      !['development', 'staging'].includes(process.env.NODE_ENV || 'production')
    ) {
      console.warn(
        'updateToolCallStatus is only available in development/staging'
      );
      return;
    }

    try {
      await fetch(
        `/dev/assistant_chats/${this.currentChat.id}/update_tool_call_status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': window.csrfToken(),
          },
          body: JSON.stringify({
            tool_call_id: toolCall.id,
            status: newStatus,
          }),
        }
      );

      // Reload the chat to reflect the updated status
      await this.loadChat(this.currentChat.id);
    } catch (error) {
      console.error('Failed to update tool call status:', error);
    }
  }
}

export default Assistant;
